import { Component, EventEmitter, Input, Output } from '@angular/core';

export type BillingHeroState =
  | 'trial'
  | 'active'
  | 'renew_window'
  | 'expired'
  | 'addon_locked'
  | 'non_admin';

@Component({
  selector: 'app-billing-hero',
  templateUrl: './billing-hero.component.html',
  styleUrl: './billing-hero.component.css',
  standalone: false,
})
export class BillingHeroComponent {
  @Input() state: BillingHeroState = 'active';
  @Input() tierName = '';
  @Input() periodLabel = '';
  @Input() seats = 0;
  @Input() activeUsers = 0;
  @Input() validUntil = '';
  @Input() daysLeft: number | null = null;
  @Input() recommendedTierName = '';
  @Input() whatsappUrl = '';
  @Input() autoRenew: boolean | null = null;
  @Input() subscriptionStatus = '';
  @Input() yearlySaveHint = '';
  /** Current per-seat price, e.g. "₹7,970 per seat / year". */
  @Input() priceLine = '';
  /** Next-charge sentence from parent when API returns amount+date; never invent client-side. */
  @Input() nextChargeLine = '';

  @Output() primary = new EventEmitter<void>();
  @Output() secondary = new EventEmitter<void>();

  /** Hide the main action while the seat checkout is already open. */
  @Input() checkoutOpen = false;

  get title(): string {
    switch (this.state) {
      case 'trial':
        return this.daysLeft != null
          ? `Your trial ends in ${this.daysLeft} days`
          : 'Your trial is ending soon';
      case 'active':
        return `${this.tierName} · ${this.periodLabel}`;
      case 'renew_window':
        return this.daysLeft != null
          ? `Your plan ends in ${this.daysLeft} days`
          : 'Your plan is ending soon';
      case 'expired':
        return 'Your plan has ended';
      case 'addon_locked':
        return `${this.tierName} · ${this.periodLabel} · ${this.seats} seats`;
      case 'non_admin':
        return 'Billing is managed by your company admin';
      default:
        return 'Billing';
    }
  }

  get body(): string {
    switch (this.state) {
      case 'trial':
        return `Start your paid plan to keep tracking your ${this.activeUsers} team members without a break.`;
      case 'active':
        return this.nextChargeLine || this.activeBody;
      case 'renew_window':
        return this.nextChargeLine
          ? `${this.nextChargeLine} Renew now to keep your team tracked.`
          : 'Renew now to keep your team tracked. You can change seats or billing period.';
      case 'expired':
        return 'Tracking is paused for your team. Reactivate to resume.';
      case 'addon_locked':
        return 'Your plan includes add-ons, so renewals and upgrades are set up with our team.';
      case 'non_admin':
        return 'Ask them to add seats or change your plan.';
      default:
        return '';
    }
  }

  get primaryLabel(): string {
    switch (this.state) {
      case 'trial':
        return 'Start paid plan';
      case 'active':
        return 'Add seats';
      case 'renew_window':
        return 'Renew plan';
      case 'expired':
        return 'Reactivate';
      case 'addon_locked':
        return 'Message us on WhatsApp';
      default:
        return '';
    }
  }

  get secondaryLabel(): string | null {
    if (this.state === 'trial') {
      return 'Compare tiers';
    }
    if (this.state === 'active' && this.recommendedTierName) {
      return `Upgrade to ${this.recommendedTierName}`;
    }
    return null;
  }

  /** Renewal date, how soon it is, and whether it renews on its own. */
  private get activeBody(): string {
    if (!this.validUntil) {
      return 'Your seat subscription is active.';
    }
    // Only say "Renews" when it actually renews on its own.
    const parts = [`${this.autoRenew === true ? 'Renews' : 'Valid until'} ${this.validUntil}`];
    if (this.daysLeft != null && this.daysLeft >= 0) {
      parts.push(
        this.daysLeft === 0 ? 'today' : `in ${this.daysLeft} day${this.daysLeft === 1 ? '' : 's'}`
      );
    }
    if (this.autoRenew === true) {
      parts.push('auto-renew on');
    } else if (this.autoRenew === false) {
      parts.push('auto-renew off');
    }
    return parts.join(' · ');
  }

  /** One plain sentence when a renewal payment needs attention. */
  get statusNotice(): string {
    switch ((this.subscriptionStatus || '').toLowerCase()) {
      case 'halted':
        return 'Your last renewal payment did not go through. Renew now to keep your team tracked.';
      case 'paused':
        return 'Your subscription is paused. Renew to resume tracking.';
      case 'pending':
        return 'Your subscription is waiting for its first payment.';
      default:
        return '';
    }
  }

  get freeSeats(): number {
    return Math.max(0, this.seats - this.activeUsers);
  }

  /** Seat tip under the meter: reassure when there is room, nudge only when nearly full. */
  get seatTip(): { text: string; tone: 'ok' | 'warn' } | null {
    if (this.seats <= 0) {
      return null;
    }
    if (this.freeSeats === 0) {
      return { text: 'All seats are in use. Add seats so new people can be tracked.', tone: 'warn' };
    }
    if (this.usedPercent >= 90) {
      return {
        text: `Only ${this.freeSeats} seat${this.freeSeats === 1 ? '' : 's'} left. Add seats before your next hire.`,
        tone: 'warn',
      };
    }
    return {
      text: `${this.freeSeats} seat${this.freeSeats === 1 ? ' is' : 's are'} free. Adding a person costs nothing extra.`,
      tone: 'ok',
    };
  }

  get showMeter(): boolean {
    return this.seats > 0 && (this.state === 'active' || this.state === 'renew_window' || this.state === 'addon_locked');
  }

  get usedPercent(): number {
    return this.seats > 0 ? Math.min(100, Math.round((this.activeUsers / this.seats) * 100)) : 0;
  }

  /** True when ≥2 seats free — used for meter tip tone only, not CTA routing. */
  get hasRoomForPeople(): boolean {
    return this.state === 'active' && this.freeSeats >= 2;
  }

  get showPrimary(): boolean {
    return this.state !== 'non_admin' && !!this.primaryLabel && !(this.checkoutOpen && this.state === 'active');
  }

  onPrimary(): void {
    this.primary.emit();
  }

  onSecondary(): void {
    this.secondary.emit();
  }
}
